import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Tag } from "lucide-react";

export const CategoriesPanel = ({ onChange }) => {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/categories");
      setCats(res.data);
    } catch (e) {
      toast.error("Kategoriler yüklenemedi");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/categories", { name: newName.trim() });
      setNewName("");
      load();
      onChange?.();
      toast.success("Kategori eklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    }
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/categories/${id}`, { name: editName.trim() });
      setEditId(null);
      load();
      onChange?.();
      toast.success("Kategori güncellendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Bu kategoriyi silmek istediğinize emin misiniz? Ürünler silinmez.")) return;
    try {
      await api.delete(`/categories/${id}`);
      load();
      onChange?.();
      toast.success("Kategori silindi");
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="max-w-xl">
      <div className="flex gap-2 mb-4">
        <input
          data-testid="new-category-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Yeni kategori adı"
          className="flex-1 bg-white border border-[var(--fb-border)] rounded-full px-4 py-2 text-sm outline-none focus:border-[var(--fb-red)]"
        />
        <button
          data-testid="add-category-btn"
          onClick={add}
          className="flex items-center gap-1 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white font-bold px-4 py-2 rounded-full transition-colors active:scale-95"
        >
          <Plus className="w-4 h-4" /> Ekle
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[var(--fb-border)] overflow-hidden">
        {cats.length === 0 ? (
          <div data-testid="categories-empty" className="p-8 text-center text-[var(--fb-muted)]">
            Henüz kategori yok.
          </div>
        ) : (
          <div className="divide-y divide-[var(--fb-border)]">
            {cats.map((c) => (
              <div
                key={c.id}
                data-testid={`category-row-${c.id}`}
                className="flex items-center gap-3 p-3 hover:bg-[var(--fb-offwhite)]"
              >
                <Tag className="w-4 h-4 text-[var(--fb-red)] shrink-0" />
                {editId === c.id ? (
                  <input
                    data-testid={`edit-category-input-${c.id}`}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit(c.id)}
                    autoFocus
                    className="flex-1 border border-[var(--fb-border)] rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[var(--fb-red)]"
                  />
                ) : (
                  <span className="flex-1 font-medium">{c.name}</span>
                )}
                {editId === c.id ? (
                  <>
                    <button
                      data-testid={`save-category-${c.id}`}
                      onClick={() => saveEdit(c.id)}
                      className="w-9 h-9 grid place-items-center rounded-full hover:bg-green-50 text-green-600"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditId(null)}
                      className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] text-[var(--fb-muted)]"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      data-testid={`edit-category-${c.id}`}
                      onClick={() => {
                        setEditId(c.id);
                        setEditName(c.name);
                      }}
                      className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] text-[var(--fb-muted)]"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-category-${c.id}`}
                      onClick={() => del(c.id)}
                      className="w-9 h-9 grid place-items-center rounded-full hover:bg-red-50 text-[var(--fb-red)]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
